/**
 * Centralized Session Management Service
 *
 * Single source of truth for ALL authentication and access control data.
 * Handles session initialization, state management, and reactive updates.
 *
 * @module sessionManager
 */

import { authStorage } from "./authStorage.service";
import { accessControl } from "./accessControl.service";
import { getUserProfileFn } from "../api/mutations/auth.mutation";
import type { User } from "../types/auth.types";

// ─────────────────────────────────────────────────────────────────────────────
// TYPE DEFINITIONS
// ─────────────────────────────────────────────────────────────────────────────

export interface Restaurant {
  id: number;
  name: string;
  locationId: number;
  locationName: string;
  isActive: boolean;
}

export interface Tenant {
  id: number;
  code: string;
  name: string;
  role: string;
  applicationId: number;
  applicationCode: string;
  applicationName: string;
}

export interface Subscription {
  id: number;
  planName: string;
  expiresAt: string;
  daysRemaining: number;
  isActive: boolean;
  status: string;
}

export interface ModuleDetail {
  moduleKey: string;
  moduleName: string;
  description: string;
  source: string;
  permissions: string[];
  category: string;
}

export interface SessionState {
  isAuthenticated: boolean;
  isInitialized: boolean;
  loading: boolean;
  token: string | null;
  restaurantId: number;
  availableFeatures: number[];
  includedModules: string[];
  user: User | null;
  modules: string[];
  permissions: string[];
  restaurant: Restaurant | null;
  tenant: Tenant | null;
  subscription: Subscription | null;
}

export interface ProfileResponse {
  success: boolean;
  message: string;
  token?: string | null;
  user: User;
  tenant: Tenant | null;
  restaurant: Restaurant | null;
  subscription: Subscription | null;
  access: {
    permissions: string[];
    modules: ModuleDetail[] | string[];
    isOwner: boolean;
    isAdmin: boolean;
    isSuperAdmin: boolean;
    hasAllLocations: boolean;
  };
}

type StateListener = (state: SessionState) => void;

// ─────────────────────────────────────────────────────────────────────────────
// SESSION MANAGER CLASS
// ─────────────────────────────────────────────────────────────────────────────

class SessionManager {
  private state: Readonly<SessionState> = {
    isAuthenticated: false,
    isInitialized: false,
    loading: true,
    token: null,
    restaurantId: 1,
    availableFeatures: [],
    includedModules: [],
    user: null,
    modules: [],
    permissions: [],
    restaurant: null,
    tenant: null,
    subscription: null,
  };

  private listeners: Set<StateListener> = new Set();

  /**
   * Initialize session from storage or backend
   * This is called on app startup
   */
  async initialize(): Promise<void> {
    const token = authStorage.getToken();

    if (!token) {
      this.clearSession();
      return;
    }

    // Check if token is expired
    if (!this.isTokenValid(token)) {
      console.warn("⚠️ Token expired, clearing session");
      this.clearSession();
      return;
    }

    // Try to load from cache first (fast — renders the app immediately)
    const cachedState = this.loadFromCache();
    if (cachedState) {
      this.state = { ...cachedState, isInitialized: true };
      this.notifyListeners();

      // Always refresh in background so that plan_status, includedModules, and
      // subscription data reflect the latest backend state on every page load.
      // This ensures that after an admin approves a plan (or any subscription
      // change), the user sees the updated status on the next page refresh
      // without needing to log out and back in.
      this.refreshInBackground();
    } else {
      // No cache, fetch from backend
      await this.fetchFromBackend();
    }
  }

  /**
   * Fetch session data from backend profile API.
   *
   * On 401: the axios interceptor intentionally skips the "Session expired"
   * toast for this endpoint (see isAuthInitEndpoint in axios.ts). We handle
   * the cleanup here by calling clearSession(), which sets isAuthenticated=false.
   * ProtectedRoute will then redirect to /login if the user is on a protected page.
   * Users on public pages (homepage etc.) will stay where they are — correct behaviour.
   *
   * On other errors: fall back to cached session data if available.
   */
  private async fetchFromBackend(): Promise<void> {
    try {
      const profile = await getUserProfileFn();
      await this.setSessionFromProfile(profile);
    } catch (error: any) {
      const status = error?.response?.status;

      if (status === 401) {
        // Session is invalid on the server side (expired, revoked, etc.)
        // Clear everything cleanly — ProtectedRoute will redirect if needed
        console.warn("⚠️ Profile fetch returned 401 — clearing session");
        this.clearSession();
        // clearSession already sets isInitialized: true and calls notifyListeners
        return;
      }

      // Non-401 error (network issue, 500, etc.): try cache as fallback
      console.error("❌ Failed to fetch profile:", error);
      const cached = this.loadFromCache();
      if (cached) {
        this.state = { ...cached, isInitialized: true };
      } else {
        this.clearSession();
        this.state = { ...this.state, isInitialized: true };
      }
      this.notifyListeners();
    }
  }

  /**
   * Refresh session data in background (non-blocking).
   * On 401: clear session — ProtectedRoute handles redirect if on a protected page.
   * On other errors: retain cached session (network blip, server issue, etc.).
   */
  private async refreshInBackground(): Promise<void> {
    try {
      const profile = await getUserProfileFn();
      await this.setSessionFromProfile(profile);
    } catch (error: any) {
      if (error?.response?.status === 401) {
        console.warn("⚠️ Background refresh returned 401 — clearing session");
        this.clearSession();
      } else {
        console.warn("⚠️ Background refresh failed, retaining cached session");
      }
    }
  }

  /**
   * Set session state from profile API response (PUBLIC)
   * Can be called by login.tsx or other components
   */
  async setSessionFromProfile(profile: ProfileResponse): Promise<void> {
    // Extract modules from response (handles both ModuleDetail[] and string[])
    const modules = this.extractModules(profile.access.modules);
    const permissions = profile.access.permissions || [];

    // Derive additional modules from permissions
    const derivedModules = this.deriveModulesFromPermissions(permissions);
    const allModules = [...new Set([...modules, ...derivedModules])];

    console.log("🔐 SessionManager.setSessionFromProfile →", {
      rawModulesFromBackend: profile.access.modules,
      extractedModuleKeys: modules,
      derivedFromPermissions: derivedModules,
      allModulesCombined: allModules,
      permissionsCount: permissions.length,
    });

    const token = profile.token || authStorage.getToken() || "";
    // Persist the fresh token immediately so axios uses it for all subsequent requests
    if (token) {
      authStorage.setToken(token);
    }
    const restaurantId = profile.restaurant?.id || 1;

    if (process.env.NODE_ENV === "development") {
    }

    // Update state
    this.state = {
      isAuthenticated: true,
      isInitialized: true,
      loading: false,
      token,
      restaurantId,
      availableFeatures: [],
      includedModules: allModules,
      user: profile.user,
      modules: allModules,
      permissions,
      restaurant: profile.restaurant,
      tenant: profile.tenant,
      subscription: profile.subscription,
    };

    // Persist to storage using authStorage (handles ModuleDetail[] properly)
    authStorage.setAccessData({
      modules: profile.access.modules,
      permissions: profile.access.permissions,
      isOwner: profile.access.isOwner,
      isAdmin: profile.access.isAdmin,
      isSuperAdmin: profile.access.isSuperAdmin,
      hasAllLocations: profile.access.hasAllLocations,
    });

    // Mirror what authStorage.storeLoginData does — store subscription and tenant
    // so that refresh() produces the same complete localStorage state as a fresh login.
    if (profile.subscription) {
      authStorage.setSubscriptionData(profile.subscription);
    }
    if (profile.tenant) {
      authStorage.setTenantData(profile.tenant);
    }

    this.persistToStorage();

    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Extract module keys from backend response
   * Handles both ModuleDetail[] and string[] formats
   */
  private extractModules(modules: ModuleDetail[] | string[]): string[] {
    if (!modules || modules.length === 0) return [];

    // Check if first item is an object (ModuleDetail)
    if (typeof modules[0] === "object" && "moduleKey" in modules[0]) {
      return (modules as ModuleDetail[]).map((m) => m.moduleKey);
    }

    // Already string array
    return modules as string[];
  }

  /**
   * Derive modules from permissions using accessControl service
   */
  private deriveModulesFromPermissions(permissions: string[]): string[] {
    // Temporarily store permissions so accessControl can read them
    localStorage.setItem("grantedPermissions", JSON.stringify(permissions));

    // Use accessControl service to derive modules
    const derived = accessControl.getModulesFromPermissions();

    return derived;
  }

  /**
   * Load session from localStorage cache
   */
  private loadFromCache(): SessionState | null {
    try {
      const token = authStorage.getToken();
      const modules = authStorage.getModules();
      const permissions = authStorage.getPermissions();
      const restaurantId = parseInt(
        localStorage.getItem("restaurantId") || "1",
      );

      if (!token || (modules.length === 0 && permissions.length === 0)) {
        return null;
      }

      // Derive modules if needed
      const derivedModules =
        modules.length > 0
          ? modules
          : this.deriveModulesFromPermissions(permissions);

      // Try to reconstruct user from localStorage
      const userStr = localStorage.getItem("user");
      const user = userStr ? JSON.parse(userStr) : null;

      return {
        isAuthenticated: true,
        isInitialized: false,
        loading: false,
        token,
        restaurantId,
        availableFeatures: [],
        includedModules: derivedModules,
        user,
        modules: derivedModules,
        permissions,
        restaurant: authStorage.getRestaurantData() as Restaurant | null,
        tenant: authStorage.getTenantData() as Tenant | null,
        subscription: (() => {
          const s = localStorage.getItem("subscription");
          return s ? JSON.parse(s) : null;
        })(),
      };
    } catch (error) {
      console.error("❌ Failed to load from cache:", error);
      return null;
    }
  }

  /**
   * Persist current session to storage
   */
  private persistToStorage(): void {
    // Store normalized modules (CRITICAL: Use modules, not includedModules)
    authStorage.setModules(this.state.modules);

    // Also store in includedModules for backward compatibility
    localStorage.setItem("includedModules", JSON.stringify(this.state.modules));
    localStorage.setItem(
      "grantedPermissions",
      JSON.stringify(this.state.permissions),
    );

    // Store user data
    if (this.state.user) {
      localStorage.setItem("user", JSON.stringify(this.state.user));
    }

    // Store restaurant ID in all expected keys for compatibility
    if (this.state.restaurant?.id) {
      localStorage.setItem("restaurantId", this.state.restaurant.id.toString());
      localStorage.setItem("businessId", this.state.restaurant.id.toString());
      localStorage.setItem("loyaltyRestaurantId", this.state.restaurant.id.toString());
    } else {
      localStorage.removeItem("restaurantId");
      localStorage.removeItem("businessId");
      localStorage.removeItem("loyaltyRestaurantId");
    }

    // Store restaurant name for bill display fallback
    if (this.state.restaurant?.name) {
      localStorage.setItem("restaurant_name", this.state.restaurant.name);
      localStorage.setItem("business_name", this.state.restaurant.name);
    } else {
      localStorage.removeItem("restaurant_name");
      localStorage.removeItem("business_name");
    }

    // Store subscription so protected-route can detect pending/active state on refresh
    if (this.state.subscription) {
      localStorage.setItem("subscription", JSON.stringify(this.state.subscription));
    } else {
      localStorage.removeItem("subscription");
    }

    // Store timestamp for cache invalidation
    localStorage.setItem("sessionTimestamp", Date.now().toString());
  }

  /**
   * Check if JWT token is valid (not expired)
   */
  private isTokenValid(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  /**
   * Subscribe to session state changes
   * Returns unsubscribe function
   */
  subscribe(listener: StateListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of state change
   */
  private notifyListeners(): void {
    this.listeners.forEach((listener) => {
      try {
        listener(this.state);
      } catch (error) {
        console.error("❌ Error in session listener:", error);
      }
    });
  }

  /**
   * Get current session state (read-only copy)
   */
  getState(): Readonly<SessionState> {
    return { ...this.state };
  }

  /**
   * Check if session is valid
   */
  isValid(): boolean {
    const token = authStorage.getToken();
    if (!token) return false;
    return this.isTokenValid(token);
  }

  /**
   * Manually refresh session from backend
   */
  async refresh(): Promise<void> {
    await this.fetchFromBackend();
  }

  /**
   * Clear session and logout
   */
  clearSession(): void {
    this.state = {
      isAuthenticated: false,
      isInitialized: true,
      loading: false,
      token: null,
      restaurantId: 1,
      availableFeatures: [],
      includedModules: [],
      user: null,
      modules: [],
      permissions: [],
      restaurant: null,
      tenant: null,
      subscription: null,
    };

    authStorage.clearAll();
    this.notifyListeners();
  }

  /**
   * Debug: Log current session info
   */
  debug(): void {
    console.group("🔐 SessionManager Debug");
    console.groupEnd();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORT SINGLETON INSTANCE
// ─────────────────────────────────────────────────────────────────────────────

export const sessionManager = new SessionManager();

// Debug helper (only in development)
if (process.env.NODE_ENV === "development") {
  (window as any).sessionManager = sessionManager;
}
